import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import MonthReport from "@/pages/MonthReport";
import Platform from "@/pages/Platform";
import Landing from "@/pages/Landing";
import Paywall from "@/pages/Paywall";
import PaymentSuccess from "@/pages/PaymentSuccess";
import Signup from "@/pages/Signup";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import Legal from "@/pages/Legal";
import ConfirmVisit from "@/pages/ConfirmVisit";
import { ErrorBoundary } from "@/components/app/ErrorBoundary";

const PlatformOnly = ({ children }) => {
  const { user } = useAuth();
  return user?.platform_role ? children : <Navigate to="/" replace />;
};

const Loading = () => (
  <div className="flex min-h-screen items-center justify-center" data-testid="app-loading">
    <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
  </div>
);

const Protected = ({ children }) => {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  return user ? children : <Navigate to="/login" replace />;
};

const Home = () => {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (!user) return <Landing />;
  return user.billing?.expired ? <Paywall /> : <Dashboard />;
};

const PublicOnly = ({ children }) => {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  return user ? <Navigate to="/" replace /> : children;
};

function App() {
  return (
    <div className="App">
      <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
            <Route path="/cadastro" element={<PublicOnly><Signup /></PublicOnly>} />
            <Route path="/esqueci-senha" element={<ForgotPassword />} />
            <Route path="/redefinir-senha" element={<ResetPassword />} />
            <Route path="/confirmar-email" element={<VerifyEmail />} />
            <Route path="/confirmar-visita" element={<ConfirmVisit />} />
            <Route path="/termos" element={<Legal kind="termos" />} />
            <Route path="/privacidade" element={<Legal kind="privacidade" />} />
            <Route path="/" element={<Home />} />
            <Route path="/assinatura/sucesso" element={<Protected><PaymentSuccess /></Protected>} />
            <Route path="/relatorio/:year/:month" element={<Protected><MonthReport /></Protected>} />
            <Route path="/painel" element={<Protected><PlatformOnly><Platform /></PlatformOnly></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster theme="dark" position="top-right" richColors />
      </AuthProvider>
      </ErrorBoundary>
    </div>
  );
}

export default App;
